'use client';

import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { BaseModal } from './BaseModal';
import { cn } from '../lib/cn';
import { resolveApiErrorToastMessage } from '../lib/api/localizeApiError';
import { updateProjectMemberRole, type TeamRole } from '../lib/api';
import { setMemberProjectRoles, type ProjectRole } from '../lib/api/projectPermissions';
import {
  buildFeatureLadders,
  diffFeatureLevels,
  summaryFeatureLevels,
  unionRolePermissions,
  type FeatureLevel,
} from '../lib/projectPermissionLevels';
import { useProjectPermissionLabels } from '../hooks/useProjectPermissionLabels';
import type { ProjectPermissionCatalogItem } from '../lib/api/projectPermissions';

type ProjectMemberRolesModalProps = {
  isOpen: boolean;
  projectId: string;
  token: string;
  memberId: string;
  memberLabel: string;
  roles: ProjectRole[];
  catalog: ProjectPermissionCatalogItem[];
  assignedRoleIds: string[];
  /** 기본 역할(built-in 등급)을 바꿀 수 있는가. legacy `PUT /members/:memberId`와 같은 `members.manage`. */
  canManageMembers: boolean;
  /** 커스텀 역할을 붙이고 뗄 수 있는가. `PUT /members/:memberId/roles`와 같은 `roles.manage`. */
  canManageRoles: boolean;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
};

const LEGACY_BUILT_IN_KEY_TO_ROLE: Partial<Record<NonNullable<ProjectRole['builtInKey']>, TeamRole>> = {
  PROJECT_OWNER: 'OWNER',
  PROJECT_ADMIN: 'ADMIN',
  PROJECT_MEMBER: 'MEMBER',
};

/**
 * 멤버의 프로젝트 역할을 바꾸는 모달.
 *
 * ## 축이 둘인 이유
 *
 * built-in 역할은 서로의 부분집합이라(OWNER ⊃ ADMIN ⊃ MEMBER ⊃ VIEWER) 실질적으로 **등급**이다.
 * 예전처럼 전부 평평한 체크박스로 두면 `Owner + Viewer` 같은 무의미한 조합이 표현 가능하고,
 * legacy `ProjectMember.role` 미러 값이 그중 무엇에서 나오는지도 화면이 설명하지 못했다.
 * 그래서 기본 역할은 단일 선택, 합집합이 실제로 의미 있는 커스텀 역할만 다중 선택으로 받는다.
 *
 * ## 권한이 갈릴 때
 *
 * `members.manage`와 `roles.manage`는 커스텀 역할에서 따로 부여될 수 있다. 등급 변경만 가능한
 * 사람에게서 그 능력을 뺏지 않으려고, 저장 경로를 권한에 맞춰 고른다 — `roles.manage`가 있으면
 * 역할 집합을 통째로 교체하고(built-in 미러는 서버가 같은 트랜잭션에서 갱신한다), 없으면 legacy
 * 역할 변경만 호출한다.
 */
export function ProjectMemberRolesModal({
  isOpen,
  projectId,
  token,
  memberId,
  memberLabel,
  roles,
  catalog,
  assignedRoleIds,
  canManageMembers,
  canManageRoles,
  onClose,
  onSaved,
}: ProjectMemberRolesModalProps) {
  const t = useTranslations('projects.roles');
  const tCommon = useTranslations('common');
  const tRoot = useTranslations();
  const { roleName, roleDescription, featureLabel, actionLabel } = useProjectPermissionLabels();

  const ladders = useMemo(() => buildFeatureLadders(catalog), [catalog]);

  const builtInRoles = useMemo(() => roles.filter((role) => role.isBuiltIn && role.archivedAt === null), [roles]);
  const customRoles = useMemo(() => roles.filter((role) => !role.isBuiltIn && role.archivedAt === null), [roles]);
  const byId = useMemo(() => new Map(roles.map((role) => [role.id, role])), [roles]);

  const initial = useMemo(() => {
    const assigned = assignedRoleIds.map((id) => byId.get(id)).filter((role): role is ProjectRole => Boolean(role));
    return {
      // 여러 built-in이 붙어 있으면 가장 강한 하나로 접는다(권한이 가장 넓은 것 = permission 수가 최대).
      baseRoleId:
        assigned.filter((role) => role.isBuiltIn).sort((a, b) => b.permissions.length - a.permissions.length)[0]?.id ??
        null,
      customRoleIds: assigned.filter((role) => !role.isBuiltIn && role.archivedAt === null).map((role) => role.id),
      // 보관된 역할이 아직 붙어 있으면 조용히 실어 보내지 않고 화면에서 알린다.
      archivedCount: assigned.filter((role) => role.archivedAt !== null).length,
    };
  }, [assignedRoleIds, byId]);

  const [baseRoleId, setBaseRoleId] = useState<string | null>(initial.baseRoleId);
  const [selectedCustom, setSelectedCustom] = useState<Set<string>>(new Set(initial.customRoleIds));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setBaseRoleId(initial.baseRoleId);
    setSelectedCustom(new Set(initial.customRoleIds));
  }, [isOpen, initial]);

  const selectedRoles = useMemo(() => {
    const picked: ProjectRole[] = [];
    const base = baseRoleId ? byId.get(baseRoleId) : undefined;
    if (base) picked.push(base);
    for (const id of selectedCustom) {
      const role = byId.get(id);
      if (role) picked.push(role);
    }
    return picked;
  }, [baseRoleId, selectedCustom, byId]);

  const beforePermissions = useMemo(
    () => unionRolePermissions(assignedRoleIds.map((id) => byId.get(id)).filter((r): r is ProjectRole => Boolean(r))),
    [assignedRoleIds, byId],
  );
  const afterPermissions = useMemo(() => unionRolePermissions(selectedRoles), [selectedRoles]);
  const diff = useMemo(
    () => diffFeatureLevels(ladders, beforePermissions, afterPermissions),
    [ladders, beforePermissions, afterPermissions],
  );

  // 저장 가능 여부는 역할 집합 변화로 판정한다. permission diff는 설명이라, 카탈로그를 읽을 권한이
  // 없는 사람(`members.manage`만 가진 경우)에게는 비어 있어서 기준으로 쓸 수 없다.
  const hasChange = useMemo(() => {
    const before = new Set(assignedRoleIds);
    const after = new Set(selectedRoles.map((role) => role.id));
    if (before.size !== after.size) return true;
    return [...after].some((id) => !before.has(id));
  }, [assignedRoleIds, selectedRoles]);

  const toggleCustom = (roleId: string) => {
    setSelectedCustom((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  };

  const canSelectBuiltInRole = (role: ProjectRole) =>
    canManageMembers && (canManageRoles || Boolean(role.builtInKey && LEGACY_BUILT_IN_KEY_TO_ROLE[role.builtInKey]));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (canManageRoles) {
        if (selectedRoles.length === 0) {
          toast.error(t('roleRequired'));
          return;
        }
        await setMemberProjectRoles(
          token,
          projectId,
          memberId,
          selectedRoles.map((role) => role.id),
        );
      } else {
        if (!baseRoleId) {
          toast.error(t('baseRoleRequired'));
          return;
        }
        // 등급만 바꿀 수 있는 사람. built-in 키를 legacy 값으로 되돌려 기존 경로로 저장한다.
        const base = byId.get(baseRoleId);
        const legacy = base?.builtInKey ? LEGACY_BUILT_IN_KEY_TO_ROLE[base.builtInKey] : undefined;
        if (!legacy) {
          toast.error(t('unsupportedBaseRole'));
          return;
        }
        await updateProjectMemberRole(token, projectId, memberId, legacy);
      }
      await onSaved();
      onClose();
    } catch (error) {
      const message = resolveApiErrorToastMessage(tRoot, error);
      if (message) toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const renderSummary = (role: ProjectRole) => {
    const levels = summaryFeatureLevels(ladders, role.permissions).slice(0, 3);
    if (levels.length === 0) return t('summaryNone');
    return levels.map((level) => `${featureLabel(level.feature)} ${actionLabel(level.action)}`).join(' · ');
  };

  const renderDiffChip = (level: FeatureLevel, tone: 'added' | 'removed', from?: string) => (
    <span
      key={`${tone}-${level.feature}-${from ?? 'none'}-${level.action}`}
      className="rounded-full px-2 py-0.5 text-[11px]"
      style={{
        background: tone === 'added' ? 'var(--chip-success-bg)' : 'var(--chip-error-bg)',
        color: tone === 'added' ? 'var(--chip-success-fg)' : 'var(--chip-error-fg)',
      }}
    >
      {tone === 'added' ? '+' : '−'} {featureLabel(level.feature)}{' '}
      {from ? `${actionLabel(from)} → ${actionLabel(level.action)}` : actionLabel(level.action)}
    </span>
  );

  const renderChangedDiffChip = (level: (typeof diff.changed)[number]) => {
    const ladder = ladders.find((candidate) => candidate.feature === level.feature);
    const isUpgrade = ladder !== undefined && ladder.actions.indexOf(level.action) > ladder.actions.indexOf(level.from);
    return renderDiffChip(level, isUpgrade ? 'added' : 'removed', level.from);
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={() => {
        if (!isSaving) onClose();
      }}
      canClose={!isSaving}
      size="lg"
      // 폼 모달이므로 bottomSheet. centered 분기는 `modal-box p-0`이라 내용이 가장자리에 붙는다.
      variant="bottomSheet"
      // 프로젝트 설정 모달 안에서 열리므로 부모보다 위 레이어여야 한다.
      zIndex="dropdown"
    >
      <div className="space-y-4">
        <div className="flex min-w-0 items-center justify-between gap-3">
          <h2 className="min-w-0 truncate text-base font-semibold">{t('assignTitle', { name: memberLabel })}</h2>
          <button
            type="button"
            className="btn btn-ghost btn-sm btn-circle shrink-0"
            aria-label={tCommon('actions.close')}
            disabled={isSaving}
            onClick={onClose}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        {initial.archivedCount > 0 ? (
          <p className="text-xs text-[var(--chip-warning-fg)]">
            {t('archivedAssignedHint', { count: initial.archivedCount })}
          </p>
        ) : null}

        <div>
          <div className="mb-2 flex items-baseline gap-2">
            <p className="text-xs font-medium text-[var(--color-fg-subtle)]">{t('baseRole')}</p>
            <p className="text-xs text-[var(--color-fg-muted)]">{t('baseRoleHint')}</p>
          </div>
          <ul className="space-y-1.5" role="radiogroup" aria-label={t('baseRole')}>
            {builtInRoles.map((role) => (
              <li key={role.id}>
                <label
                  className={cn(
                    'flex cursor-pointer items-start gap-2.5 rounded-lg border p-2.5',
                    baseRoleId === role.id ? 'border-primary bg-primary/5' : 'border-base-300 hover:bg-base-200',
                    !canSelectBuiltInRole(role) && 'cursor-not-allowed opacity-60',
                  )}
                >
                  <input
                    type="radio"
                    className="radio radio-xs mt-0.5"
                    name="project-member-base-role"
                    aria-label={roleName(role)}
                    checked={baseRoleId === role.id}
                    disabled={!canSelectBuiltInRole(role)}
                    onChange={() => setBaseRoleId(role.id)}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm">{roleName(role)}</span>
                    <span className="block text-xs text-[var(--color-fg-muted)]">{roleDescription(role)}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="mb-2 flex items-baseline gap-2">
            <p className="text-xs font-medium text-[var(--color-fg-subtle)]">{t('additionalRoles')}</p>
            <p className="text-xs text-[var(--color-fg-muted)]">{t('additionalRolesHint')}</p>
          </div>
          {customRoles.length === 0 ? (
            <p className="text-xs text-[var(--color-fg-muted)]">{t('noAdditionalRoles')}</p>
          ) : (
            <ul className="max-h-40 space-y-1.5 overflow-y-auto">
              {customRoles.map((role) => (
                <li key={role.id}>
                  <label
                    className={cn(
                      'flex cursor-pointer items-start gap-2.5 rounded-lg border p-2.5',
                      selectedCustom.has(role.id) ? 'border-primary bg-primary/5' : 'border-base-300 hover:bg-base-200',
                      !canManageRoles && 'cursor-not-allowed opacity-60',
                    )}
                  >
                    <input
                      type="checkbox"
                      className="checkbox checkbox-xs mt-0.5"
                      aria-label={role.name}
                      checked={selectedCustom.has(role.id)}
                      disabled={!canManageRoles}
                      onChange={() => toggleCustom(role.id)}
                    />
                    <span className="min-w-0">
                      <span className="block text-sm">{role.name}</span>
                      <span className="block text-xs text-[var(--color-fg-muted)]">{renderSummary(role)}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* 카탈로그를 읽을 수 없으면 사다리가 비어 diff를 계산할 수 없다. 빈 상자를 그리는 대신 감춘다. */}
        {ladders.length > 0 ? (
          <div className="rounded-xl border border-base-300 p-3">
            <p className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]">{t('changeSummary')}</p>
            <div className="flex flex-wrap gap-1.5" data-testid="permission-change-summary">
              {diff.added.length + diff.changed.length + diff.removed.length > 0 ? (
                <>
                  {diff.added.map((level) => renderDiffChip(level, 'added'))}
                  {diff.changed.map(renderChangedDiffChip)}
                  {diff.removed.map((level) => renderDiffChip(level, 'removed'))}
                </>
              ) : (
                <span className="text-xs text-[var(--color-fg-muted)]">{t('noChange')}</span>
              )}
            </div>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <button type="button" className="btn btn-ghost btn-sm" disabled={isSaving} onClick={onClose}>
            {tCommon('actions.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={isSaving || !hasChange}
            onClick={() => void handleSave()}
          >
            {isSaving ? tCommon('loading') : tCommon('actions.save')}
          </button>
        </div>
      </div>
    </BaseModal>
  );
}
